import React, { useMemo, useState } from "react";
import { ABtn, AResult, AScore } from "./arcadeUi";

const N = 3; // 3x3 boxes -> 4x4 dots
const edgesOfBox = (r, c) => [`h-${r}-${c}`, `h-${r + 1}-${c}`, `v-${r}-${c}`, `v-${r}-${c + 1}`];
const adjBoxes = (id) => {
  const [t, a, b] = id.split("-").map((x, i) => (i === 0 ? x : +x));
  const res = [];
  if (t === "h") { if (a - 1 >= 0) res.push([a - 1, b]); if (a <= N - 1) res.push([a, b]); }
  else { if (b - 1 >= 0) res.push([a, b - 1]); if (b <= N - 1) res.push([a, b]); }
  return res;
};
const ALL_EDGES = (() => {
  const e = [];
  for (let r = 0; r <= N; r++) for (let c = 0; c < N; c++) e.push(`h-${r}-${c}`);
  for (let r = 0; r < N; r++) for (let c = 0; c <= N; c++) e.push(`v-${r}-${c}`);
  return e;
})();
const sides = (r, c, edges) => edgesOfBox(r, c).filter((e) => edges[e]).length;

export default function DotsAndBoxes() {
  const [edges, setEdges] = useState({});
  const [boxes, setBoxes] = useState({});
  const [turn, setTurn] = useState("you"); // you | cpu
  const [busy, setBusy] = useState(false);

  const scores = useMemo(() => {
    let y = 0, c = 0;
    Object.values(boxes).forEach((o) => (o === "you" ? y++ : c++));
    return { y, c };
  }, [boxes]);
  const over = Object.keys(boxes).length === N * N;

  const applyMove = (E, B, id, who) => {
    E[id] = who;
    let completed = false;
    for (const [br, bc] of adjBoxes(id)) {
      const bid = `b-${br}-${bc}`;
      if (B[bid]) continue;
      if (edgesOfBox(br, bc).every((e) => E[e])) { B[bid] = who; completed = true; }
    }
    return completed;
  };

  const cpuMove = (E) => {
    const avail = ALL_EDGES.filter((id) => !E[id]);
    // 1. completing move
    for (const id of avail) if (adjBoxes(id).some(([r, c]) => sides(r, c, E) === 3)) return id;
    // 2. safe move (doesn't give a 3rd side)
    const safe = avail.filter((id) => adjBoxes(id).every(([r, c]) => sides(r, c, E) + 1 !== 3));
    const pool = safe.length ? safe : avail;
    return pool[Math.floor(Math.random() * pool.length)];
  };

  const runCpu = (startEdges, startBoxes) => {
    setBusy(true);
    let E = { ...startEdges }, B = { ...startBoxes };
    const step = () => {
      if (Object.keys(B).length === N * N) { setEdges(E); setBoxes(B); setBusy(false); return; }
      const id = cpuMove(E);
      const completed = applyMove(E, B, id, "cpu");
      setEdges({ ...E }); setBoxes({ ...B });
      if (completed && Object.keys(B).length < N * N) setTimeout(step, 500);
      else { setBusy(false); setTurn("you"); }
    };
    setTimeout(step, 450);
  };

  const play = (id) => {
    if (edges[id] || busy || turn !== "you" || over) return;
    const E = { ...edges }, B = { ...boxes };
    const completed = applyMove(E, B, id, "you");
    setEdges(E); setBoxes(B);
    if (!completed && Object.keys(B).length < N * N) { setTurn("cpu"); runCpu(E, B); }
  };

  const reset = () => { setEdges({}); setBoxes({}); setTurn("you"); setBusy(false); };

  // build 7x7 cell grid
  const cells = [];
  for (let i = 0; i <= 2 * N; i++) {
    for (let j = 0; j <= 2 * N; j++) {
      const er = i % 2 === 0, ec = j % 2 === 0;
      if (er && ec) cells.push(<span key={`d${i}${j}`} className="m-auto h-2.5 w-2.5 rounded-full bg-kirana-brown" />);
      else if (er && !ec) {
        const id = `h-${i / 2}-${(j - 1) / 2}`;
        cells.push(<button key={id} data-testid={`db-${id}`} onClick={() => play(id)} className="group m-auto flex h-full w-full items-center justify-center">
          <span className={`h-1.5 w-full rounded-full transition-colors ${edges[id] ? "bg-kirana-brown" : "bg-kirana-brown/10 group-hover:bg-kirana-red/50"}`} /></button>);
      } else if (!er && ec) {
        const id = `v-${(i - 1) / 2}-${j / 2}`;
        cells.push(<button key={id} data-testid={`db-${id}`} onClick={() => play(id)} className="group m-auto flex h-full w-full items-center justify-center">
          <span className={`h-full w-1.5 rounded-full transition-colors ${edges[id] ? "bg-kirana-brown" : "bg-kirana-brown/10 group-hover:bg-kirana-red/50"}`} /></button>);
      } else {
        const bid = `b-${(i - 1) / 2}-${(j - 1) / 2}`;
        const o = boxes[bid];
        cells.push(<span key={bid} className={`m-0.5 flex items-center justify-center rounded-sm font-signboard text-lg font-bold ${o === "you" ? "bg-kirana-red/80 text-white" : o === "cpu" ? "bg-kirana-green/80 text-white" : ""}`}>{o === "you" ? "You" : o === "cpu" ? "CPU" : ""}</span>);
      }
    }
  }

  return (
    <div className="w-full text-center">
      <div className="flex justify-center gap-2">
        <AScore label="You" value={scores.y} />
        <AScore label="CPU" value={scores.c} />
        <span className="rounded-md border-2 border-kirana-brown bg-white px-2.5 py-1 text-sm font-bold text-kirana-brown">{over ? "Done" : busy ? "CPU…" : "Your turn"}</span>
      </div>
      <p className="my-2 text-sm text-kirana-brown/70">Tap a line. Close the 4th side to win a box (and go again).</p>
      <div className="mx-auto w-full max-w-[300px]" data-testid="db-board" style={{ aspectRatio: "1/1" }}>
        <div className="grid h-full w-full" style={{ gridTemplateColumns: `14px repeat(${N}, 1fr 14px)`, gridTemplateRows: `14px repeat(${N}, 1fr 14px)` }}>
          {cells}
        </div>
      </div>
      {over && (
        <>
          <AResult testId="db-result" win={scores.y > scores.c ? true : scores.y === scores.c ? null : false}
            text={scores.y > scores.c ? "You Win! 🎉" : scores.y === scores.c ? "It's a Tie 🤝" : "CPU Wins 😜"} />
          <div className="mt-3 flex justify-center"><ABtn data-testid="db-again" onClick={reset} tone="green">Play Again</ABtn></div>
        </>
      )}
    </div>
  );
}
